import { useEffect, useCallback, useState } from "react";
import config from "appConfig";
import { buildBaseUrl } from "../../services";
import { getRelativeReturnUrl } from "../../history";

interface CredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleOneTapProps {
  isLoggedIn: boolean;
  profileLoading?: boolean;
}

const GoogleOneTap = ({ isLoggedIn, profileLoading }: GoogleOneTapProps) => {
  const [hasGdprConsent, setHasGdprConsent] = useState(false);

  const handleCredentialResponse = useCallback((response) => {
    if (response.credential) {
      const returnUrl = getRelativeReturnUrl();
      const googleOneTapUrl = buildBaseUrl(
        `/authentication/google-one-tap?returnUrl=${encodeURIComponent(returnUrl)}&token=${encodeURIComponent(response.credential)}`
      );
      window.location.href = googleOneTapUrl;
    }
  }, []);

  // Listen for Didomi GDPR consent
  useEffect(() => {
    const w = window;

    const checkDidomiConsent = () => {
      const Didomi = w["Didomi"];
      if (Didomi && typeof Didomi.getUserStatus === 'function') {
        const userStatus = Didomi.getUserStatus();
        // Check if user has actually enabled consent for required purposes
        // purposes.consent.enabled contains the list of purposes the user has consented to
        if (userStatus && userStatus.purposes && userStatus.purposes.consent &&
            userStatus.purposes.consent.enabled && userStatus.purposes.consent.enabled.length > 0) {
          setHasGdprConsent(true);
          return true;
        }
      }
      return false;
    };

    // Check if already consented
    if (checkDidomiConsent()) {
      return;
    }

    // Wait for Didomi to be ready and user to make a choice
    const didomiOnReady = w["didomiOnReady"] || [];
    w["didomiOnReady"] = didomiOnReady;

    didomiOnReady.push(function(Didomi) {
      // Listen for consent changes
      Didomi.on('consent.changed', function() {
        // Re-check if user actually enabled consent (they might have declined)
        checkDidomiConsent();
      });

      // Also check if consent was already given before we registered the listener
      if (checkDidomiConsent()) {
        return;
      }
    });
  }, []);

  useEffect(() => {
    // Wait for profile endpoint to resolve before deciding whether to show One Tap
    if (profileLoading) {
      return;
    }

    // Don't show Google One Tap if user is already logged in
    if (isLoggedIn) {
      return;
    }

    // Wait for GDPR consent before showing Google One Tap
    if (!hasGdprConsent) {
      return;
    }

    if (!config.googleClientId || config.googleClientId === "YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com") {
      console.warn("Google One Tap: Client ID not configured");
      return;
    }

    const initializeGoogleOneTap = () => {
      const google = window.google;
      if (google && google.accounts && google.accounts.id) {
        google.accounts.id.initialize({
          client_id: config.googleClientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: "signin"
        });

        google.accounts.id.prompt(function(notification) {
          if (notification.isNotDisplayed()) {
            console.log("Google One Tap not displayed:", notification.getNotDisplayedReason());
          } else if (notification.isSkippedMoment()) {
            console.log("Google One Tap skipped:", notification.getSkippedReason());
          } else if (notification.isDismissedMoment()) {
            console.log("Google One Tap dismissed:", notification.getDismissedReason());
          }
        });
      }
    };

    const google = window.google;

    // Check if the Google library is already loaded
    if (google && google.accounts && google.accounts.id) {
      initializeGoogleOneTap();
    } else {
      // Wait for the library to load
      var checkGoogleLoaded = setInterval(function() {
        var g = window.google;
        if (g && g.accounts && g.accounts.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleOneTap();
        }
      }, 100);

      // Clean up interval after 10 seconds if library doesn't load
      var timeoutId = setTimeout(function() {
        clearInterval(checkGoogleLoaded);
      }, 10000);

      return function() {
        clearInterval(checkGoogleLoaded);
        clearTimeout(timeoutId);
      };
    }

    return function() {
      var g = window.google;
      if (g && g.accounts && g.accounts.id) {
        g.accounts.id.cancel();
      }
    };
  }, [isLoggedIn, profileLoading, hasGdprConsent, handleCredentialResponse]);

  return null;
};

export default GoogleOneTap;



// WEBPACK FOOTER //
// ./src/components/GoogleOneTap/index.tsx